import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { jsx as _jsx } from "react/jsx-runtime";
import { BaseSocialLoginButton } from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/BaseSocialLoginButton";
import { defaultSSOErrorMessage, extractMessage } from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/errorMessages";
import { useHandleSSOSuccess } from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/useHandleSSOSuccess";
import { useScriptCheck } from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/useScriptCheck";
import { isGoogleLoginSciptLoaded, loadGoogleLoginScript } from "@faire/retailer-visitor-shared/initializers/loadGoogleLoginScript";
import { getGoogleSSOClientID } from "@faire/retailer-visitor-shared/serialized-data/getGoogleSSOClientID";
import { useSSOErrorStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOErrorStore";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { logError } from "@faire/web--source/common/logging";
import getIDToken from "@faire/web-api--source/api/v2/users/google/oauth-token/post";
import { SocialSignOnProvider } from "@faire/web-api--source/indigofair/data/SocialSignOnProvider";
import React, { useCallback, useEffect } from "react";
var PROVIDER = SocialSignOnProvider.GOOGLE;
var SCOPE = "openid profile email";
var useCodeClientRef = function() {
    return React.useRef(undefined);
};
export var GoogleLoginButtonV2 = function(props) {
    var disabled = props.disabled, isOnSignIn = props.isOnSignIn, sourceId = props.sourceId;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useScriptCheck = useScriptCheck({
        isScriptLoaded: isGoogleLoginSciptLoaded,
        loadScript: loadGoogleLoginScript,
        provider: PROVIDER
    }), isScriptLoading = _useScriptCheck.isScriptLoading, hasScriptError = _useScriptCheck.hasScriptError;
    var handleSSOSuccess = useHandleSSOSuccess(_object_spread_props(_object_spread({}, props), {
        provider: PROVIDER
    }));
    var codeClientRef = useCodeClientRef();
    var googleSSOClientID = getGoogleSSOClientID();
    var _useSSOErrorStore = useSSOErrorStore(), errorMessage = _useSSOErrorStore.errorMessage, setErrorMessage = _useSSOErrorStore.setErrorMessage;
    var _useSSOStore = useSSOStore(), userClickedSSOButton = _useSSOStore.userClickedSSOButton, recordSSOButtonClick = _useSSOStore.recordSSOButtonClick;
    var onSuccess = useCallback(/*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(param) {
            var code, sso_token, idToken;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        code = param.code;
                        return [
                            4,
                            getIDToken({
                                authorization_code: code
                            })
                        ];
                    case 1:
                        sso_token = _state.sent().sso_token;
                        idToken = sso_token === null || sso_token === void 0 ? void 0 : sso_token.id_token;
                        if (idToken) {
                            handleSSOSuccess(idToken);
                        } else {
                            setErrorMessage(defaultSSOErrorMessage(strictLocalize));
                        }
                        return [
                            2
                        ];
                }
            });
        });
        return function(_) {
            return _ref.apply(this, arguments);
        };
    }(), [
        handleSSOSuccess,
        setErrorMessage,
        strictLocalize
    ]);
    var onFailure = useCallback(function(errorResponse) {
        var error = errorResponse.error;
        logError(errorResponse, {
            tags: {
                signInType: "SSO",
                provider: PROVIDER,
                code: error
            }
        });
        if (error) {
            var errorMessage = extractMessage({
                provider: PROVIDER,
                code: error
            }, strictLocalize);
            setErrorMessage(errorMessage);
        }
    }, [
        setErrorMessage,
        strictLocalize
    ]);
    var handleResponse = useCallback(function(response) {
        return response.error ? onFailure(response) : onSuccess(response);
    }, [
        onFailure,
        onSuccess
    ]);
    // Initialise
    useEffect(function() {
        if (!isScriptLoading) {
            try {
                var _windowObj_google_accounts, _windowObj_google;
                var windowObj = getWindowOrThrow();
                if ((windowObj === null || windowObj === void 0 ? void 0 : (_windowObj_google = windowObj.google) === null || _windowObj_google === void 0 ? void 0 : (_windowObj_google_accounts = _windowObj_google.accounts) === null || _windowObj_google_accounts === void 0 ? void 0 : _windowObj_google_accounts.oauth2) && typeof (windowObj === null || windowObj === void 0 ? void 0 : windowObj.google.accounts.oauth2.initCodeClient) === "function") {
                    /* eslint-disable-next-line react-compiler/react-compiler */ codeClientRef.current = google.accounts.oauth2.initCodeClient({
                        client_id: googleSSOClientID !== null && googleSSOClientID !== void 0 ? googleSSOClientID : "",
                        scope: SCOPE,
                        callback: function(response) {
                            return handleResponse(response);
                        }
                    });
                }
            } catch (error) {
                logError(error, {
                    tags: {
                        signInType: "SSO",
                        provider: PROVIDER,
                        code: "auth_init_failed"
                    }
                });
                setErrorMessage(defaultSSOErrorMessage(strictLocalize));
            }
        }
    }, [
        isScriptLoading,
        googleSSOClientID,
        setErrorMessage,
        handleResponse,
        codeClientRef,
        strictLocalize
    ]);
    if (hasScriptError) {
        return /*#__PURE__*/ _jsx(BaseSocialLoginButton, {
            onClick: function() {
                return setErrorMessage(strictLocalize({
                    id: "zwErny",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Google script is blocked. Unblock and refresh to use Google sign in."
                        }
                    ]
                }));
            },
            socialProvider: PROVIDER,
            disabled: disabled,
            sourceId: sourceId
        });
    }
    if (!userClickedSSOButton && errorMessage) {
        return /*#__PURE__*/ _jsx(BaseSocialLoginButton, {
            onClick: recordSSOButtonClick,
            socialProvider: PROVIDER,
            disabled: disabled,
            isOnSignIn: isOnSignIn,
            sourceId: sourceId
        });
    }
    return /*#__PURE__*/ _jsx(BaseSocialLoginButton, {
        disabled: disabled,
        onClick: function() {
            var _codeClientRef_current;
            return (_codeClientRef_current = codeClientRef.current) === null || _codeClientRef_current === void 0 ? void 0 : _codeClientRef_current.requestCode();
        },
        socialProvider: PROVIDER,
        loading: isScriptLoading,
        isOnSignIn: isOnSignIn,
        sourceId: sourceId
    });
};
